import React, {useCallback, useMemo} from "react";
import {
    Box,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";

const PREVIEW_ROW_COUNT = 2;
const MAX_COLUMNS = 12;

const REQUIREMENT_CHIP = {
    required: {color: 'error', key: 'requirementRequired'},
    optional: {color: 'default', key: 'requirementOptional'},
    conditional: {color: 'warning', key: 'requirementConditional'},
};

const ColumnTooltipContent = ({column, t}) => {
    const chipMeta = REQUIREMENT_CHIP[column.requirement] ?? REQUIREMENT_CHIP.optional;

    return (
        <Box sx={{p: 0.5, maxWidth: 300}}>
            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {column.title}
            </Typography>
            <Chip
                label={t(chipMeta.key)}
                size="small"
                color={chipMeta.color}
                variant="outlined"
                sx={{height: 22, fontSize: '0.7rem', mb: column.description || column.note ? 1 : 0}}
            />
            {column.description && (
                <Typography variant="body2" sx={{lineHeight: 1.5}}>
                    {column.description}
                </Typography>
            )}
            {column.note && (
                <Typography variant="caption" display="block" sx={{mt: 0.75, color: 'text.secondary'}}>
                    {column.note}
                </Typography>
            )}
        </Box>
    );
};

const ColumnTooltipCell = ({column, children, t}) => (
    <Tooltip
        title={<ColumnTooltipContent column={column} t={t} />}
        arrow
        placement="top"
        enterDelay={200}
        componentsProps={{
            tooltip: {
                sx: {maxWidth: 320},
            },
        }}
    >
        {children}
    </Tooltip>
);

/**
 * MUI-based spreadsheet preview for import column schemas.
 */
const DatasetTablePreview = ({columns}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport.dataSourceCatalog'});

    const displayColumns = useMemo(() => (columns ?? []).slice(0, MAX_COLUMNS), [columns]);

    const previewRows = useMemo(() => (
        Array.from({length: PREVIEW_ROW_COUNT}, (_, rowIndex) => (
            displayColumns.map((col) => {
                if (rowIndex === 0) {
                    return col.sampleValue || '—';
                }
                return rowIndex === 1 && col.sampleValue ? '' : '—';
            })
        ))
    ), [displayColumns]);

    const headerCellSx = useCallback(() => ({
        fontWeight: 600,
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        bgcolor: 'background.default',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 1,
        cursor: 'help',
    }), []);

    const bodyCellSx = useCallback((cell) => ({
        fontSize: '0.8125rem',
        color: cell && cell !== '—' ? 'text.secondary' : 'text.disabled',
        fontStyle: cell && cell !== '—' ? 'normal' : 'italic',
        whiteSpace: 'nowrap',
        py: 0.875,
        bgcolor: 'background.paper',
        cursor: 'help',
    }), []);

    if (!displayColumns.length) {
        return null;
    }

    return (
        <Box sx={{mt: 2}}>
            <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1}}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                    {t('previewTitle')}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                    {t('columnTooltipHint')}
                </Typography>
            </Box>
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 1,
                    borderColor: 'divider',
                    overflowX: 'auto',
                    bgcolor: 'background.paper',
                }}
            >
                <Table size="small" sx={{minWidth: 480, bgcolor: 'background.paper'}}>
                    <TableHead>
                        <TableRow>
                            {displayColumns.map((col) => (
                                <ColumnTooltipCell key={col.key} column={col} t={t}>
                                    <TableCell sx={headerCellSx()}>
                                        {col.title}
                                        {col.requirement !== 'optional' && (
                                            <Typography component="span" color="error.main" sx={{ml: 0.25}}>
                                                *
                                            </Typography>
                                        )}
                                    </TableCell>
                                </ColumnTooltipCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {previewRows.map((row, rowIndex) => (
                            <TableRow
                                key={`preview-row-${rowIndex}`}
                                sx={{
                                    bgcolor: 'background.paper',
                                    '&:last-child td': {borderBottom: 0},
                                }}
                            >
                                {row.map((cell, cellIndex) => {
                                    const col = displayColumns[cellIndex];
                                    return (
                                        <ColumnTooltipCell key={`${col.key}-${rowIndex}`} column={col} t={t}>
                                            <TableCell sx={bodyCellSx(cell)}>
                                                {cell || '—'}
                                            </TableCell>
                                        </ColumnTooltipCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {(columns?.length ?? 0) > displayColumns.length && (
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', textAlign: 'center', mt: 0.75}}>
                    {t('moreColumns', {count: columns.length - displayColumns.length})}
                </Typography>
            )}
        </Box>
    );
};

export default DatasetTablePreview;
